import type WebApiClient from "../../api/WebApiClient.js";
import { LOAD_TRANSCRIPT_FAILURE, LOAD_TRANSCRIPT_REQUEST, LOAD_TRANSCRIPT_SUCCESS } from "../action-types.js";
import { type IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { type Transcript } from "@/graphql.ts";

/**
 * State definition
 */

export interface ITranscriptState {
  transcript?: Transcript;
  isLoading: boolean;
}

const initialState: ITranscriptState = {
  isLoading: false,
  transcript: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_TRANSCRIPT_REQUEST]: (state: ITranscriptState, _payload: Transcript): ITranscriptState => {
    return { ...state, isLoading: true };
  },
  [LOAD_TRANSCRIPT_SUCCESS]: (state: ITranscriptState, payload: Transcript): ITranscriptState => {
    return { ...state, isLoading: false, transcript: payload };
  },
  [LOAD_TRANSCRIPT_FAILURE]: (state: ITranscriptState, _payload): ITranscriptState => {
    return { ...state, isLoading: false, transcript: null };
  },
});

/**
 * Action creators
 */
let lastFetchedTrandscriptId = -1;

export function shouldLoadTranscript(transcriptId: number) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<Transcript> => {
    const state: IAppState = getState();
    lastFetchedTrandscriptId = transcriptId;

    // fetch the transcript only if no loading is currently in progress and transcript is either absent or doesn't match the transcript ID
    if (
      !state.transcript.isLoading &&
      (!state.transcript.transcript || state.transcript.transcript.id !== transcriptId)
    ) {
      dispatch({ type: LOAD_TRANSCRIPT_REQUEST });

      return webApiClient
        .fetchTranscript(transcriptId)
        .then((response: Transcript) => {
          if (lastFetchedTrandscriptId === response.id) {
            dispatch({
              type: LOAD_TRANSCRIPT_SUCCESS,
              payload: response,
            });

            return response;
          }

          return state.transcript.transcript;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: LOAD_TRANSCRIPT_FAILURE,
            alertTitle: "Fetching of Transcript Failed",
          }),
        );
    }
  };
}
